import { useState, useEffect, useRef } from "react";
import { message, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import sortBy from "lodash/sortBy";
import { useTranslation } from "react-i18next";
import { useEventBus } from "../../../../lib/components/EventBusProvider";
import db from "../../../../database";
import { timeStamp } from "../../../../utils/clock";
import { generateDocumentNo } from "../../../../utils/documentSequenceUtils";
import { shouldRegenerateIdentityAtSave, captureOrderCreationSnapshot } from "../../../../utils/cartIdentityGuard";
import { assertDocumentNoUnique } from "../../../../utils/dataIntegrity";
import { logSequenceEvent } from "../../../../utils/sequenceAudit";
import { getWorkflowData } from "../../../../utils/getWorkflowData";
import { resolveActingUserId } from "../../../../utils/actingUser";
import { assignCartLineKeys } from "../../../../utils/pos/assignCartLineKeys";
import { stock } from "../stock";
import { discardCouponService, discardGiftCard, upsertGiftCardTransaction, upsertCart, upsertEstimate } from "../../../../services";
import messages from "../../../../errors/messages.en-IN.json";
import useSessionStore from "../../../../stores/useSessionStore";
import useSafeTimeout from "../../../../hooks/useSafeTimeout";
import useTransactionStore from "../../../../stores/useTransactionStore";

const useParkedBills = ({
  cart,
  setCart,
  deleteCart,
  deleteReceipt,
  orderType,
  giftCardData,
  setGiftCardData,
  documentSequence,
  setDocumnetSequence,
  tillData,
  upsertPOSLog,
  posConfig,
  authHeaders,
  getFilteredPaymentMethods,
  setShowPayments,
  setSelectedDocumentType,
  setProductSearchInput,
  setOpen,
  searchInputRef,
  setShowPaymentMethods,
  setOrderHistoryDetails,
  setSelectedProductInCart,
  setProductStock,
  setTempCart,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  setManagementScreenShow,
  setSelectedSaleType,
  setKeyValue,
  handleSelectedSaleTypeForProducts,
  handleLoyaltyPayment,
  hasShownCustomerModalRef,
  isExchange,
  setIsparkedBill,
  tillaccess,
  metaDataRef,
  history,
}) => {
  const { t } = useTranslation();
  const { dispatchEvent } = useEventBus();
  const { safeSetTimeout } = useSafeTimeout();

  const storedParkedList = useSessionStore.getState().parkedList;
  const initialParkedList = storedParkedList ? storedParkedList : [];
  const [displayParkedBillModal, setDisplayParkedBillModal] = useState(false);
  const [parkedList, setParkedList] = useState(initialParkedList);
  const [filterdParkedList, setFilterdParkedList] = useState(initialParkedList);
  const [parkedBillSearchInput, setParkedBillSearchInput] = useState("");

  const parkedBillInFlightRef = useRef(false);

  const parkedListRef = useRef(initialParkedList);
  useEffect(() => {
    parkedListRef.current = parkedList;
  }, [parkedList]);

  const mapParkedList = (list) => {
    return list.map((item) => ({
      ...item,
      customer: item?.parkedCart?.customer?.name || "",
      status: "Parked",
      key: item.key,
      qty: item?.parkedCart?.totalQty || 0,
    }));
  };

  const discardParkedBill = async (record) => {
    // BUG-1858: a parked Layaway/Advance bill with a real deposit already collected
    // must go through a return/refund, not a silent discard — mirrors the guard
    // already present in usePreorderHandlers.js/useSuperMarketHandlers.js
    // cancelOrder(), which this path never had. Also checks paymentData, not just
    // payments/advancePayment: a retrieved advance order (useOrderHistory.js) loads
    // its historical deposit into paymentData while resetting payments to [], so
    // paymentData is the only place the real amount is still visible by then.
    const parkedCart = record?.parkedCart || {};
    const hasAdvancePayment = Number(parkedCart?.advancePayment || 0) > 0;
    const hasRecordedPayments = parkedCart?.payments?.some((p) => Number(p.amount || 0) > 0);
    const hasHistoricalPaymentData = parkedCart?.paymentData?.some((p) => Number(p.amount || 0) > 0);
    if ((parkedCart?.layAway === "Y" || parkedCart?.type === "Layaway") && (hasAdvancePayment || hasRecordedPayments || hasHistoricalPaymentData)) {
      message.error("This order has existing payments and cannot be discarded. Please process a return or refund instead.");
      return;
    }

    upsertPOSLog(record.parkedCart, "DPO");

    // Remove from store / main list
    const localParkedList = useSessionStore.getState().parkedList || [];
    const updatedList = localParkedList.filter((item) => item.key !== record.key);
    useSessionStore.getState().setParkedList(updatedList);
    setParkedList([...updatedList]);

    // Update filtered list based on current search input
    if (!parkedBillSearchInput) {
      setFilterdParkedList(mapParkedList(updatedList));
    } else {
      const lowercaseKey = parkedBillSearchInput.toLowerCase();
      const filteredBills = updatedList.filter((item) => {
        const lowercaseCustomer = item?.parkedCart?.customer?.name?.toLowerCase() || "";
        const lowercaseParkedDocNo = item.parkedDocNo?.toLowerCase() || "";
        return lowercaseCustomer.includes(lowercaseKey) || lowercaseParkedDocNo.includes(lowercaseKey);
      });
      setFilterdParkedList(mapParkedList(filteredBills));
    }

    // Save discarded cart to DB
    const tillSession = useSessionStore.getState().tillSession;
    const tillSessionId = tillSession?.tillSessionId;
    // BUG-1969 (defect #5): must clone, not reference. Walk-in carts' .customer is
    // tillData.tillAccess.csBunit.b2cCustomer -- one object shared by reference across
    // every cart on this till for the whole session. captureOrderCreationSnapshot below
    // mutates cartToDb.customer in place; without a clone, that permanently corrupts the
    // shared b2cCustomer object on the first discard, freezing a stale customerAddressIds
    // onto every subsequent walk-in order this session. Matches the clone pattern already
    // used at the other write sites (e.g. useOrderProcessing.js's cartToDb assignment).
    let cartToDb = JSON.parse(JSON.stringify(record.parkedCart));
    cartToDb.orderTime = dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss");
    cartToDb.createdBy = resolveActingUserId(tillData); // BUG-2232: acting cashier, not till-registration user
    cartToDb.orderType = orderType?.cwrSaletype?.cwrSaletypeId || "";
    // TSK-3249: order stays under the till's opening business date until store close.
    cartToDb.orderDate = tillSession?.businessDate ? dayjs(tillSession.businessDate).format("DD-MM-YYYY") : dayjs(new Date()).format("DD-MM-YYYY");
    cartToDb.tillSessionId = tillSessionId;
    cartToDb.key = uuidv4().replace(/-/g, "").toUpperCase();
    cartToDb.isSynced = 0;
    cartToDb.syncAttempts = 0;
    cartToDb.customerSearchKey = cart?.customer?.code || "";
    cartToDb.total = 0;
    cartToDb.totalQty = 0;
    cartToDb.tax = 0;
    cartToDb.isReturn = false;
    // BUG-1528: recurse into Custom Mix / grouped items' children too — their
    // real qty/price live on item.children, not the parent. A flat forEach
    // zeroed only the parent, leaving children's original weight/nettotal
    // intact on a discarded parked bill. Mirrors useSuperMarketHandlers.js's
    // resetItemValues (and the same fix applied to usePreorderHandlers.js).
    const resetItemValues = (item) => {
      item.salePrice = 0;
      item.sunitprice = 0;
      item.netStd = 0;
      item.nettotal = 0;
      item.unitTax = 0;
      item.taxAmount = 0;
      item.weight = 0;
      item.unitPrice = 0;
      item.netList = 0;
      item.discount = 0;
      item.isReturn = false;
      item.linetax = 0;
      item.linenet = 0;
      item.linegross = 0;
      item.linestd = 0;
      item.netunit = 0;
      item.listPrice = 0;
      item.grossunit = 0;
      item.grossstd = 0;
      item.grosslist = 0;
      item.realPrice = 0;
      if (Array.isArray(item.children) && item.children.length > 0) {
        item.children.forEach((child) => resetItemValues(child));
      }
    };
    cartToDb.items.forEach((item) => resetItemValues(item));
    const cancelledSeq = useSessionStore.getState().cancelledOrderSequence;
    if (cancelledSeq) {
      cartToDb.documentno = generateDocumentNo(cancelledSeq);
      useSessionStore.getState().incrementCancelledOrderSequence();
    }
    cartToDb.paid = 0;
    cartToDb.payments = [];
    cartToDb.change = 0;
    cartToDb.discount = 0;
    cartToDb.couponInput = [];
    // DI-31 (discard-parked-bill path): the cancelled-sequence documentno above (when
    // generated) comes from a purely local counter that can drift out of sync with
    // numbers other tills/sessions already assigned to real orders — the same class
    // of gap that produced duplicate invoice numbers on the regular completion path
    // (see useOrderProcessing.js DI-31, RAMS/21320 and related production incidents).
    // Re-validate here before persisting; non-blocking, self-heals via
    // assertDocumentNoUnique. No-op when no cancelled sequence is configured.
    if (cancelledSeq) {
      try {
        const discardDocDupCheck = await assertDocumentNoUnique({
          dbAdapter: db,
          documentno: cartToDb.documentno,
          sOrderID: cartToDb.sOrderID,
          prefix: cancelledSeq.prefix,
          sessionStore: useSessionStore,
          sequenceType: "cancelled",
        });
        if (discardDocDupCheck.recovered) {
          cartToDb.documentno = discardDocDupCheck.documentno;
        }
      } catch (discardDocErr) {
        console.warn("[DI-31] Discard-path documentno uniqueness check failed (non-blocking):", discardDocErr?.message);
      }
    }
    // DI-33 (discard-parked-bill path): same unguarded-overwrite class as the cancel
    // handlers — a parked snapshot normally has no db.orders row yet (parking doesn't
    // write one), so this is a no-op in the common case. It only fires if this
    // sOrderID collides with an already-completed real sale, in which case discarding
    // must not overwrite it.
    try {
      const existingOrderForDiscard = cartToDb.sOrderID ? await db.orders.get(cartToDb.sOrderID) : null;
      const preorderPrefixDiscard = useSessionStore.getState().preorderSequence?.prefix;
      if (
        shouldRegenerateIdentityAtSave({
          existingOrder: existingOrderForDiscard,
          cart: cartToDb,
          documentno: cartToDb.documentno,
          preorderPrefix: preorderPrefixDiscard,
        })
      ) {
        const staleSOrderID = cartToDb.sOrderID;
        cartToDb.sOrderID = uuidv4().replace(/-/g, "").toUpperCase();
        console.warn("[DI-33] Stale sOrderID detected at discard-parked-bill — regenerated to prevent order overwrite.", {
          staleSOrderID,
          newSOrderID: cartToDb.sOrderID,
          documentno: cartToDb.documentno,
          overwrittenDocumentno: existingOrderForDiscard.documentno,
        });
        logSequenceEvent(
          "stale-identity-recovery",
          {
            documentno: cartToDb.documentno,
            staleSOrderID,
            newSOrderID: cartToDb.sOrderID,
            existingDocumentno: existingOrderForDiscard.documentno,
            flow: "discard-parked-bill",
          },
          db
        );
        // BUG-2175: same gap as the cancel paths (SuperMarket/Restaurant/Preorder) —
        // regenerating only sOrderID stops this write from overwriting
        // existingOrderForDiscard's row, but this discard record would still save under
        // that row's OLD documentno without also re-checking uniqueness (two intact orders
        // sharing one receipt number). Same fix already applied to processOrderApproved
        // (useOrderProcessing.js) for the identical gap. Falls back to the till's regular
        // sequence — this branch only fires when the identity is genuinely stale, at which
        // point cartToDb.documentno is no longer trustworthy as a parked-series number
        // (shouldRegenerateIdentityAtSave's own Layaway/isRetrived exemptions above already
        // ruled out the legitimate "still parked" case before reaching here).
        try {
          const discardIdentityDocDupCheck = await assertDocumentNoUnique({
            dbAdapter: db,
            documentno: cartToDb.documentno,
            sOrderID: cartToDb.sOrderID,
            prefix: tillData.tillAccess.cwrTill.prefix,
            sessionStore: useSessionStore,
            sequenceType: "regular",
          });
          if (discardIdentityDocDupCheck.recovered) {
            cartToDb.documentno = discardIdentityDocDupCheck.documentno;
          }
        } catch (discardIdentityDocErr) {
          console.warn("[DI-33] Post-regeneration documentno uniqueness check failed at discard-parked-bill (non-blocking):", discardIdentityDocErr?.message);
        }
      }
    } catch (diErr) {
      console.warn("[DI-33] Stale identity check failed at discard-parked-bill (non-blocking):", diErr?.message);
    }
    // Issue-7 / FK-3692,6319: see captureOrderCreationSnapshot's own docblock.
    captureOrderCreationSnapshot(cartToDb);
    await db.orders.put(cartToDb);
  };

  const retrieveParkedData = async (record) => {
    const parkedCart = record?.parkedCart || {};
    const saleTypeId = parkedCart?.saleType?.cwrSaletypeId;

    try {
      const saleTypes = await db.posSaletypes.toArray();
      const saleTypeData = saleTypes.find((item) => item.cwrSaletype?.cwrSaletypeId === saleTypeId);

      if (saleTypeData?.cwrSaletype?.name) {
        setSelectedSaleType(saleTypeData.cwrSaletype.name);
      }

      if (cart.items.length > 0) {
        openDisplayParkedBillModal("retrieve", record);
      } else {
        selectParkedBill(record, "management");
      }
    } catch (err) {
      console.error("Error retrieving sale type from DB:", err);
      message.error(`${messages.info.retrieve_saletype_failed}`);
    }
  };

  const openDisplayParkedBillModal = (param, record) => {
    if (useTransactionStore.getState().selectedReturnType === "EXCHANGE") {
      message.warning("Hold is not allowed during Return & Exchange.");
      return;
    }
    if (cart.items.length > 0) {
      Modal.confirm({
        title: "Save Cart Items ?",
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            You can retrieve the bill later by selecting the 'Retrieve' option in Parked Bills.
            <br />
            Do you want to continue parking the bill?
          </div>
        ),
        okText: "Yes",
        cancelText: "No",
        autoFocusButton: null,
        okButtonProps: { style: { display: "inline-block" } },
        async onOk() {
          if (cart?.estimateId) {
            try {
              const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
              const estimateId = cart.estimateId;
              const date = dayjs().format("YYYY-MM-DD HH:mm:ss");
              const response = await upsertEstimate({ bunitId, estimateId, date, status: "PE", isAdvanceOrder: "N", estimateJson: cart });
              if (response?.data?.data?.upsertEstimate?.status === "200") {
                message.success(response?.data?.data?.upsertEstimate?.message);
                deleteCart();
              } else {
                message.error("Something is wrong in upsertEstimate service");
              }
            } catch (err) {
              console.error("Error parking estimate:", err);
            }
            return;
          }
          if (posConfig?.saveParkedBills === "Y") {
            const csBunitId = tillData?.tillAccess?.csBunit?.csBunitId;
            const cartItems = cart;
            const cwrPosCartId = cartItems?.cwr_pos_cart_id || uuidv4().replace(/-/g, "").toUpperCase();
            const cwrTillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
            const status = "DR";

            const cartPayload = {
              csBunitId,
              cwrPosCartId,
              cwrTillId,
              status,
              cartItems,
            };

            const response = await upsertCart(cartPayload);
            if (response?.status === 200) {
              if (response?.data?.data?.upsertCart?.status === "200") {
                message.success(response?.data?.data?.upsertCart?.message);
                deleteReceipt();
              } else if (response?.data?.data?.upsertCart?.status === "201") {
                message.error(response?.data?.data?.upsertCart?.message);
              } else {
                message.error("Something is wrong in upsertCart service");
              }
            } else {
              message.warning("Something is wrong in upsertCart service");
            }
          } else {
            setSelectedDocumentType("Invoice");
            let PaymentMethods = getFilteredPaymentMethods() || [];
            let payments =
              PaymentMethods.length > 5
                ? sortBy(PaymentMethods, "sequenceNo").slice(
                    0,
                    tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
                  )
                : PaymentMethods;

            setShowPayments(payments);
            parkBill(param, record);
            const productSearchInput = document.getElementById("sm-product-search");
            safeSetTimeout(() => {
              setOpen(false);
            }, 600);
            if (searchInputRef?.current) {
              searchInputRef?.current?.focus();
            }
            setProductSearchInput("");
          }
        },
        onCancel() {
          const productSearchInput = document.getElementById("sm-product-search");
          productSearchInput?.focus();
          if (searchInputRef?.current) {
            searchInputRef.current.focus();
          }
          setOpen(false);
          setProductSearchInput("");
          setDisplayParkedBillModal(true);
        },
      });
    } else {
      setProductSearchInput("");
      setDisplayParkedBillModal(true);
    }
  };

  const discardGiftCards = async (cart) => {
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) {
      setAuthTokens = authHeaders.access_token;
    }
    if (giftCardData.length > 0) {
      let details = [];
      cart?.giftCardData?.map((ele) => {
        details.push(`{
            cardNo: ${ele.number ? `"${ele.number}"` : null}
            referenceNo: "${ele.refId}"
                }`);
      });
      discardGiftCard(null, null, details);
      let upsertDetails = [];
      giftCardData.map((ele) => {
        upsertDetails.push(`{
            cardNo: ${ele.number ? `"${ele.number}"` : null}
            type: "RD"
            referenceNo: "${cart.referenceId}"
            amount: ${ele.amount * -1}
            cardPin: ${ele?.pin ? `"${ele?.pin}"` : null}
            tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
            b2c_Customer_Id: ${cart.customer.cwrCustomerId ? `"${cart.customer.cwrCustomerId}"` : null}
                }`);
      });
      const data = {
        query: `
            mutation {
              upsertGiftCardTransaction(giftCardTransactions: [${upsertDetails}]) {
                status
                message
              }
            }`,
      };
      await upsertGiftCardTransaction(data);
    }
  };

  const processWithParkedBillCancellation = (record) => {
    let setAuthTokens;
    if (authHeaders && authHeaders.access_token) {
      setAuthTokens = authHeaders.access_token;
    }
    discardGiftCards(record.parkedCart);
    if (record?.parkedCart?.couponInput?.length > 0) {
      const serverUrl = process.env.REACT_APP_serverUrl;

      const couponsData = [...record.parkedCart.couponInput];
      // Function to discard a coupon
      const discardCoupon = async (coupon) => {
        const discardCouponQuery = {
          query: `query {
              discardCoupon(couponcode: "${coupon.couponCode}", referenceId: "${coupon.referenceId}") {
                status
                message
              }
            }`,
        };
        try {
          const response = await discardCouponService(coupon.couponCode, coupon.referenceId);
        } catch (error) {
          console.error(`Error discarding coupon ${coupon.couponCode}:`, error);
        }
      };
      couponsData.forEach((coupon) => {
        discardCoupon(coupon);
      });
    }
    let index = record.parkedCart.payments.findIndex((payment) => payment.name.toLowerCase() === "loyalty" && !record.parkedCart.isReturn);
    if (index >= 0) {
      handleLoyaltyPayment(record.parkedCart.payments[index], record.parkedCart);
    }
    setGiftCardData([]);
    discardParkedBill(record);
    setParkedBillSearchInput("");

    // Refresh the parked bills list
    const allParkedList = useSessionStore.getState().parkedList || [];
    setFilterdParkedList(mapParkedList(allParkedList));
    const productSearchInput = document.getElementById("sm-product-search");
    if (productSearchInput) {
      safeSetTimeout(() => {
        productSearchInput.focus();
      }, 300);
    }
  };

  const handleOk = async (record) => {
    if (parkedBillInFlightRef.current) return;
    parkedBillInFlightRef.current = true;
    try {
    const updatedItems = record?.parkedCart?.items?.map((item) => ({
      ...item,
      realWeight: item.weight,
    }));

    const updatedRecord = {
      ...record,
      parkedCart: {
        ...record.parkedCart,
        items: updatedItems,
      },
    };
    const { workflowRules, approvers } = await getWorkflowData();
    const cancelParkedBillRule = workflowRules.find((res) => res.eventName === "Cancel Parked Bill");
    if (cancelParkedBillRule) {
      dispatchEvent(cancelParkedBillRule.eventName, cancelParkedBillRule.ruleName, cancelParkedBillRule.ruleCondition, approvers, null, async (eventData) => {
        if (eventData?.approvalGranted) {
          await processWithParkedBillCancellation(updatedRecord);
        } else {
          Modal.error({
            title: "Approval Required",
            content: "The cancellation request was not approved.",
          });
        }
      });
    } else {
      await processWithParkedBillCancellation(updatedRecord);
    }
    } finally {
      parkedBillInFlightRef.current = false;
    }
  };

  const handleCancel = () => {
    // Handle the cancel action here
    Modal.destroyAll();
    const productSearchInput = document.getElementById("sm-product-search");
    if (productSearchInput) {
      safeSetTimeout(() => {
        productSearchInput.focus();
      }, 300);
    }
  };

  const discardMessage = (modalTitle, modalContent, record) => {
    Modal.confirm({
      title: modalTitle,
      content: modalContent,
      onOk: () => handleOk(record),
      okButtonProps: { style: { display: "inline-block" } },
      autoFocusButton: null,
      onCancel: handleCancel,
    });
  };

  const parkBill = (value, record) => {
    if (cart.parked !== "Y" && cart.layAway !== "Y") {
      const pbCounter = useSessionStore.getState().incrementParkedBillCounter();
      cart.documentno = `PB/${String(pbCounter).padStart(6, "0")}`;
      deleteReceipt("parked");
      if (value === "retrieve") {
        selectParkedBill(record, "management");
      }
    } else if (cart.layAway === "Y") {
      // Parking a retrieved advance order — keep the existing ADV/ (or PRE/) documentno.
      // Overwriting with a PB/ counter would orphan the original advance sequence number
      // and cause a new ADV/ to be generated on the next "Create Advance".
      deleteReceipt("parked");
      // BUG-1916: this branch never re-entered selectParkedBill for the target record,
      // unlike its sibling branches above/below. Retrieving a different parked bill while
      // the active cart was itself an in-progress advance order parked the active cart but
      // silently dropped the requested retrieval — the target bill never loaded into cart,
      // leaving selectedDocumentType stuck on the "Invoice" set moments earlier in
      // openDisplayParkedBillModal's onOk, so the retrieved advance order could not be
      // completed or cancelled.
      if (value === "retrieve") {
        selectParkedBill(record, "management");
      }
    } else {
      if (value === "retrieve") {
        selectParkedBill(record, "management");
      } else {
        deleteReceipt("parked");
      }
    }
    const presentParkedList = value === "parkKey" ? parkedListRef.current : useSessionStore.getState().parkedList || [];
    cart.parked = "Y";
    const presentCart = cart;
    const presentTimeStamp = timeStamp();
    const parkedBill = {
      parkedCart: presentCart,
      parkedTime: presentTimeStamp,
      parkedDocNo: cart.documentno,
      parkedOrderID: cart.items[0].sOrderReturnId,
      parkedBillId: uuidv4().replace(/-/g, "").toUpperCase(),
      qty: presentCart.totalQty,
    };
    presentParkedList.push(parkedBill);
    useSessionStore.getState().setParkedList([...presentParkedList]);
    setParkedList([...presentParkedList]);
    hasShownCustomerModalRef.current = false;

    {
      message.success(`${t("bill_parking_successful")}`);
    }
  };

  const selectParkedBill = async (item, fieldName) => {
    // Re-entrancy guard: a double-click/double-tap on "Retrieve" (common on touchscreen
    // POS terminals) can fire this twice before the first call's awaited stock check
    // resolves and splices the bill out of parkedList below — both calls would then read
    // the same still-present entry and both hand it to a cart, producing two carts that
    // share one sOrderID/documentno (the exact duplicate-invoice class fixed elsewhere
    // this session). Reuses parkedBillInFlightRef (e48471a3, "Fix 92 double-submit
    // vulnerabilities") rather than a separate ref — that commit already guarded
    // discardParkedBill/handleOk against exactly this class of race but missed this
    // retrieve path; one shared guard across all parkedList-mutating actions is also
    // correct defense against those OTHER actions racing this one, not just against
    // this action racing itself. Not confirmed in production; closed defensively since
    // the shape is identical to every other stale-identity gap already fixed.
    if (parkedBillInFlightRef.current) return;
    parkedBillInFlightRef.current = true;
    try {
    const listItemIndex = parkedList.findIndex((bill) => bill.parkedBillId === item.parkedBillId);
    if (listItemIndex === -1) return; // already retrieved by a prior/concurrent call
    const selectedParkedBill = {
      ...parkedList[listItemIndex].parkedCart,
      isRetrived: "Y",
      // GPR-live incident (2026-08-28): cartIdentityGuard's stale-identity check
      // needs a config-independent "still the same order" signal — see
      // cartIdentityGuard.js's retrievedDocumentno comment for the full rationale.
      retrievedDocumentno: parkedList[listItemIndex].parkedCart?.documentno,
    };

    let stockCheckResult = true;
    let isFinalValidation = false;
    let isCompleteSale = false;
    // A bill can be parked while it still holds an untouched "AD"-segment item
    // (e.g. cashier added an advance-deposit line, then parked instead of
    // completing). Retrieving it must not drop straight into payment below --
    // that skips handleCartTotalClick, the only place that blocks completing a
    // sale with an advance item present, and the bill would get paid off and
    // submitted as a plain Invoice on the regular doc sequence instead of
    // routing through the dedicated Advance flow. Mirrors handleCartTotalClick's
    // own check (usePaymentFlow.js) rather than duplicating its full guard,
    // using selectedParkedBill.layAway (the same flag that already decides the
    // "Handle layaway logic" branch below) instead of the live selectedDocumentType
    // state, since that state describes whatever was on-screen before this
    // retrieve, not the bill being retrieved.
    const hasBlockingAdvanceItem = selectedParkedBill?.layAway !== "Y" && selectedParkedBill?.items?.some((it) => it?.productSegment === "AD" && it?.weight > 0);
    // BUG-2275: Preorder items are intentionally added without a stock check (see the
    // matching !isPreOrderPage gate in cart.js) since preorder allows adding items that
    // aren't currently in stock. Re-running a live stock check here on retrieve treated
    // that as a fresh failure and blocked retrieving the parked preorder with "Stock is
    // not available", even though the item was never meant to be validated at add time.
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (!isPreOrderPage && navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y") {
      stockCheckResult = await stock({
        cart: selectedParkedBill,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
        isCompleteSale: true,
        setShowCompleteSaleStock,
        setCompleteSaleStockData,
      });
      if (!stockCheckResult) {
        setSelectedProductInCart({});
        return;
      } else if (hasBlockingAdvanceItem) {
        message.warning(`${messages.errors.cart.advance_option_required}`);
        setOrderHistoryDetails([]);
        setIsparkedBill(false);
      } else {
        setShowPaymentMethods(true);
        setOrderHistoryDetails([]);
        setIsparkedBill(false);
      }
    } else if (hasBlockingAdvanceItem) {
      message.warning(`${messages.errors.cart.advance_option_required}`);
      setOrderHistoryDetails([]);
      setIsparkedBill(false);
    } else {
      setShowPaymentMethods(true);
      setOrderHistoryDetails([]);
      setIsparkedBill(false);
    }
    // Remove the selected bill from the parked list. Re-derive the index from the live
    // store state rather than trusting the pre-await listItemIndex — the two can diverge
    // if another park/retrieve happened while the stock check above was in flight.
    const liveParkedList = useSessionStore.getState().parkedList || [];
    const liveIndex = liveParkedList.findIndex((bill) => bill.parkedBillId === item.parkedBillId);
    if (liveIndex === -1) return; // already retrieved by a concurrent call while awaiting stock check
    const updatedParkedList = [...liveParkedList];
    updatedParkedList.splice(liveIndex, 1);
    setParkedList(updatedParkedList);
    useSessionStore.getState().setParkedList(updatedParkedList);

    // Handle layaway logic
    if (selectedParkedBill?.layAway === "Y") {
      setSelectedDocumentType("Advance");

      const PaymentMethods = getFilteredPaymentMethods() || [];
      const maxPayments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
            )
          : PaymentMethods;

      setShowPayments(maxPayments);
    }
    if (!isExchange) {
      setCart(selectedParkedBill);
    }
    // BUG-1916 root cause: this navigated to /preorder whenever the retrieved bill
    // carried a deliveryDate, regardless of whether the till has PreOrder access
    // (posConfig.enablePreOrder). ModuleGate's own "preorder" check (accessControl.js)
    // requires enablePreOrder === "Y" — on a till without it, this push immediately
    // bounced back to /pos, unmounting and remounting PointOfsaleCoreGuard's whole
    // subtree (confirmed via trace: Guard UNMOUNT + Guard mount within 5ms of the
    // ModuleGate redirect). That remount reset useCoreState's selectedDocumentType
    // back to its default "Invoice", even though setSelectedDocumentType("Advance")
    // had already fired correctly moments earlier — the cart survived (persisted
    // store) but the doc-type flag didn't (plain component state), leaving the
    // retrieved advance bill stuck unable to complete/cancel.
    if (selectedParkedBill?.deliveryDate && history && posConfig?.enablePreOrder === "Y" && !window.location.pathname.includes("/preorder")) {
      history.push("/preorder");
    }
    setDisplayParkedBillModal(false);
    if (fieldName === "management") {
      setManagementScreenShow(false);
    }
    } finally {
      parkedBillInFlightRef.current = false;
    }
  };

  const selectLayAwayOrder = async (item, fieldName) => {
    const itemWithNumberTotal = {
      ...item,
      total: parseFloat(item.total),
    };
    const customMixEntry = metaDataRef.current.find((entry) => entry.key === "Custom Mix");
    // BUG: getSalesHistoryData (useOrderHistory.js) already reconstructs the Custom Mix
    // parent/children grouping from this same "Custom Mix" metaData entry before this
    // record ever reaches here — see its "Reconstruct Custom Mix parent-child grouping"
    // block, which replaces the flat lines with a synthetic parent (sOrderlineID: null)
    // carrying the real children nested inside. Re-running the grouping below against
    // that already-grouped item.items makes every sOrderlineID match fail (the flat
    // lines no longer exist at the top level), producing a second "Custom Mix" row with
    // blank/NaN children while the original, already-grouped parent falls through
    // untouched via unmatchedItems — two duplicate rows in the cart. Skip re-grouping
    // whenever item.items already contains a grouped entry.
    const alreadyGrouped = item.items?.some((line) => Array.isArray(line.children) && line.children.length > 0);
    let updatedCart;
    if (!customMixEntry || alreadyGrouped) {
      updatedCart = {
        ...itemWithNumberTotal,
      };
    } else {
      let customMixData = [];
      try {
        customMixData = JSON.parse(customMixEntry.value);
      } catch (error) {
        console.error("Invalid JSON in Custom Mix value", error);
        return [];
      }
      const matchedIds = new Set();
      const enrichedMixes = customMixData.map((mix) => {
        const matchedChildren = mix.children
          .map((child) => {
            const matchedItem = item.items.find((line) => line.sOrderlineID === child.sOrderlineID);
            if (matchedItem) {
              matchedIds.add(child.sOrderlineID);
            }
            return {
              ...matchedItem,
              taxAmount: matchedItem?.unittax ?? 0,
              groupedItems: true,
              actualWeight: matchedItem?.weight / mix.weight,
              baseQty: matchedItem?.weight / mix.weight,
            };
          })
          .filter(Boolean);

        return {
          ...mix,
          children: matchedChildren,
          nettotal: mix.nettotal,
          sunitprice: mix.nettotal,
          originalPrice: mix.nettotal,
          salePrice: mix.nettotal - mix.taxAmount,
          realPrice: mix.nettotal - mix.taxAmount,
          addNewLine: "N",
          batchedForSale: "N",
          batchedForStock: "N",
          batchedProduct: "N",
          batchno: null,
          bunitName: "",
          csBunitId: null,
          customAttributes: [],
          preparationTime: "",
          printerId: "",
          cwrMenuTimeslot: [],
          description: "",
          discount: mix.discount,
          discountName: "",
          foodType: "",
          itemType: "",
          grosslist: 0,
          grossstd: 0,
          grossunit: 0,
          hsncode: "",
          imageUrl: "",
          imageurl: "",
          isDecimal: false,
          isDecimalQty: false,
          isManualQty: false,
          isPromoApplicable: false,
          isQtyDesimal: mix?.uomData?.[0]?.stdprecision ?? 2,
          isReturn: false,
          key: 0,
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          linegross: 0,
          linenet: 0,
          linetax: 0,
          listPrice: 0,
          listprice: 0,
          mBatchId: null,
          mPricingruleId: null,
          multiPrice: "N",
          name2: "",
          netList: 0,
          netStd: 0,
          netunit: 0,
          newCustomAttributes: [],
          order: "N",
          originalPrice: 0,
          priceList: [],
          primaryOrderLine: null,
          productAddons: [],
          productCategoryName: "",
          productId: uuidv4().replace(/-/g, "").toUpperCase(),
          productManufacturerId: null,
          productionCenter: "",
          productionCenterId: "",
          returnQty: null,
          salesRepId: null,
          salesRepName: "",
          selectedAddons: [],
          shelfLife: "",
          shortDescription: "",
          showAddons: "N",
          taxCategory: "",
          taxFlag: "N",
          taxName: "",
          type: "I",
          unitPrice: 0,
          unitTax: 0,
          upc: "",
          upcPricingRule: null,
          value: "",
        };
      });
      const unmatchedItems = item.items.filter((line) => !matchedIds.has(line.sOrderlineID));
      const allItems = [...enrichedMixes, ...unmatchedItems];
      updatedCart = {
        ...itemWithNumberTotal,
        items: allItems,
      };
    }
    // See useCoreEffects.js's clearStaleCart — on a till with no configured
    // preorderSequence prefix, the mount-time stale-cart guard can't tell this
    // legitimate retrieve hand-off apart from a crash leftover and wipes it.
    // This stamp is that guard's escape hatch.
    updatedCart.justRetrievedAt = Date.now();
    // BUG-2020: selectParkedBill already stamps isRetrived:"Y" (line ~609 above) so the
    // "can't delete the only item" guard (useCartOperations.js:376) treats a retrieved
    // parked bill like a parked bill. This function retrieves Advance orders too (from
    // Manage Pre-Order's "Retrieve Order"/"Complete Sale") and never stamped it, so that
    // same guard silently didn't apply -- the last item on a retrieved Advance order
    // could be deleted, leaving an empty cart, unlike every other retrieval path.
    // Audited every other isRetrived consumer before adding this (cartIdentityGuard.js's
    // two branches, useOrderProcessing.js's resolvedDocumentNo/DI-31 checks, both
    // cancel-parked-bill workflow gates): all of them already take the same branch via
    // cart.type==="Layaway" (set just above/by the caller) independent of isRetrived, or
    // are neutralized by this fleet's empty preorderSequence prefix -- confirmed no
    // behavior change to any of them from this stamp.
    updatedCart.isRetrived = "Y";
    // GPR-live incident (2026-08-28): same retrieval-snapshot stamp as
    // selectParkedBill above — see cartIdentityGuard.js's retrievedDocumentno
    // comment. Captured before anything below can reassign updatedCart.documentno.
    updatedCart.retrievedDocumentno = updatedCart.documentno;
    // Order-history lines have no `key`; without it every row click resolves to line 1.
    updatedCart = assignCartLineKeys(updatedCart);
    setShowPaymentMethods(true);
    const filteredItemsForStockCheck = updatedCart.items.filter((item) => item.productSegment !== "CB" && item.type === "I");
    const stockCart = {
      ...updatedCart,
      items: filteredItemsForStockCheck,
    };
    let stockCheckResult = true;
    let isFinalValidation = false;
    if (navigator.onLine && updatedCart?.preOrder === "Y" && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y") {
      stockCheckResult = await stock({
        cart: stockCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation,
        setTempCart,
      });
      if (!stockCheckResult) {
        setSelectedProductInCart({});
        return;
      } else {
        setCart(updatedCart);
      }
    }
    setCart(updatedCart);
    if (tillaccess?.layout === "2") {
      setKeyValue("2");
      const saleData = await db.posSaletypes.toArray();

      const selectedSaleTypeRecord = saleData.find((item) => item.cwrSaletype.cwrSaletypeId === updatedCart.saleType?.cwrSaletypeId);
      if (selectedSaleTypeRecord) {
        handleSelectedSaleTypeForProducts(selectedSaleTypeRecord);
      }
    }
  };

  const handleParkedBillSearchInput = (e) => {
    setParkedBillSearchInput(e.target.value);
    searchParkedBill(e.target.value);
  };

  const searchParkedBill = (key) => {
    const allParkedList = useSessionStore.getState().parkedList || [];
    if (!key) {
      setFilterdParkedList(mapParkedList(allParkedList));
      return;
    }

    const lowercaseKey = key.toLowerCase();

    const filteredBills = allParkedList.filter((list) => {
      const lowercaseCustomer = list?.parkedCart?.customer?.name?.toLowerCase() || "";
      const lowercaseParkedDocNo = list.parkedDocNo?.toLowerCase() || "";
      return lowercaseCustomer.includes(lowercaseKey) || lowercaseParkedDocNo.includes(lowercaseKey);
    });
    setFilterdParkedList(mapParkedList(filteredBills));
  };

  const closeParkedBillModal = () => {
    setDisplayParkedBillModal(false);
    setParkedBillSearchInput("");
    setFilterdParkedList([...parkedList]);
  };

  return {
    // State
    displayParkedBillModal,
    setDisplayParkedBillModal,
    parkedList,
    setParkedList,
    filterdParkedList,
    setFilterdParkedList,
    parkedBillSearchInput,
    setParkedBillSearchInput,
    parkedListRef,

    // Handlers
    openDisplayParkedBillModal,
    parkBill,
    selectParkedBill,
    selectLayAwayOrder,
    discardParkedBill,
    retrieveParkedData,
    discardGiftCards,
    processWithParkedBillCancellation,
    searchParkedBill,
    handleParkedBillSearchInput,
    mapParkedList,
    closeParkedBillModal,
    discardMessage,
    handleOk,
    handleCancel,
  };
};

export default useParkedBills;
